import { Navigate, Route, Routes } from 'react-router-dom';
import { Suspense, lazy } from 'react';
import AppShell from './Shell/AppShell';
import { readStoredUser, useWorkspace } from './Shell/Workspace';
import { getRecent } from './Projects/recent';
import { Spinner, ToastProvider } from './ui';
import Login from './Login/Login';
import Guard from './Shell/Guard';

// After a deploy the hashed chunk files change; a tab still running the previous build would get a 404
// (ChunkLoadError) and crash. Reload once instead so it picks up the new build.
const lazyReload = (factory) =>
  lazy(() =>
    factory().catch(() => {
      if (!window.__gqChunkReload) {
        window.__gqChunkReload = 1;
        window.location.reload();
      }
      return new Promise(() => {});
    })
  );

// Route-level code splitting keeps the first paint small (Cinema/Calendar/Chat are heavy).
const Dashboard = lazyReload(() => import('./Stats_dashboard/Dashboard'));
const ProjectsPage = lazyReload(() => import('./Projects/ProjectsPage'));
const ProjectLayout = lazyReload(() => import('./Projects/ProjectLayout'));
const ForcePassword = lazyReload(() => import('./Login/ForcePassword'));
const AiUsagePage = lazyReload(() => import('./AIUsage/AiUsagePage'));
const Performance = lazyReload(() => import('./Performance/Performance'));
const ReviewPage = lazyReload(() => import('./Performance/ReviewPage'));
const EmailPage = lazyReload(() => import('./Admin/EmailPage'));
const Organization = lazyReload(() => import('./Hr/Organization'));
const Leads = lazyReload(() => import('./Leads/Leads'));
const Notes = lazyReload(() => import('./Notes/Notes'));
const AutoGenerate = lazyReload(() => import('./AutoGenerate/AutoGenerate'));
const Office = lazyReload(() => import('./Office/Office'));
const Calendar = lazyReload(() => import('./Scheduler/Calendar'));
const Chat = lazyReload(() => import('./Chat/Chat'));
const Meeting = lazyReload(() => import('./Meeting/Meeting'));
const Cinema = lazyReload(() => import('./Cinema/Cinema'));
const Robot = lazyReload(() => import('./Robot/Robot_GPT'));
const People = lazyReload(() => import('./Users_dashboard/People'));
const Profile = lazyReload(() => import('./Profile/Profile'));
const AccessPage = lazyReload(() => import('./Access/AccessPage'));
const FilesPage = lazyReload(() => import('./Files/FilesPage'));
const ApiClientPage = lazyReload(() => import('./ApiClient/ApiClientPage'));
const PermissionsPage = lazyReload(() => import('./Admin/PermissionsPage'));
const AuditPage = lazyReload(() => import('./Admin/AuditPage'));

// Old /tasks links (bookmarks, notifications) land on the last opened project.
function TasksRedirect() {
  const { projects } = useWorkspace();
  const last = getRecent()[0];
  const p = projects.find((x) => x._id === last) || projects[0];
  const search = window.location.search;
  if (!projects.length) return <Spinner />;
  return <Navigate to={p ? `/projects/${p.key}/board${search.replace('task=', 'selectedIssue=' + p.key + '-')}` : '/projects'} replace />;
}

function RequireAuth({ children }) {
  const user = readStoredUser();
  if (!(user && user.token)) return <Navigate to="/" replace />;
  return user.mustChangePassword ? <ForcePassword /> : children; // a temporary password must be replaced before anything else
}

export default function App() {
  const stored = readStoredUser() || {};
  const authed = !!stored.token;
  return (
    <Suspense fallback={<Spinner />}>
      <Routes>
        <Route path="/" element={authed ? <Navigate to={stored.isExternal ? '/projects' : '/dashboard'} replace /> : <Login />} />
        <Route
          element={
            <RequireAuth>
              <AppShell />
            </RequireAuth>
          }
        >
          <Route path="/dashboard" element={<Guard screen="dashboard"><Dashboard /></Guard>} />
          <Route path="/projects" element={<Guard screen="projects"><ProjectsPage /></Guard>} />
          <Route path="/projects/:key/*" element={<Guard screen="projects"><ProjectLayout /></Guard>} />
          <Route path="/tasks" element={<TasksRedirect />} />
          <Route path="/office" element={<Guard screen="office"><Office /></Guard>} />
          <Route path="/scheduler" element={<Guard screen="calendar"><Calendar /></Guard>} />
          <Route path="/notes" element={<Guard screen="notes"><Notes /></Guard>} />
          <Route path="/performance" element={<Performance />} />
          <Route path="/performance/:id" element={<ReviewPage />} />
          <Route path="/admin/email" element={<Guard screen="email"><EmailPage /></Guard>} />
          <Route path="/organization" element={<Guard screen="hr"><Organization /></Guard>} />
          <Route path="/leads" element={<Guard screen="leads"><Leads /></Guard>} />
          <Route path="/autogen" element={<Guard screen="autogen"><AutoGenerate /></Guard>} />
          <Route path="/chat" element={<Guard screen="chat"><Chat /></Guard>} />
          <Route path="/cinema" element={<Guard screen="cinema"><Cinema /></Guard>} />
          <Route path="/robot" element={<Guard screen="assistant"><Robot /></Guard>} />
          <Route path="/userManagement" element={<Guard screen="workers"><People /></Guard>} />
          <Route path="/access/*" element={<Guard screen="access"><AccessPage /></Guard>} />
          <Route path="/apiclient" element={<Guard screen="apiclient"><ApiClientPage /></Guard>} />
          <Route path="/library" element={<Guard screen="library"><FilesPage /></Guard>} />
          <Route path="/admin/permissions" element={<Guard screen="permissions" adminOnly><PermissionsPage /></Guard>} />
          <Route path="/admin/ai-usage" element={<Guard screen="aiusage"><AiUsagePage /></Guard>} />
          <Route path="/admin/audit" element={<Guard screen="audit"><AuditPage /></Guard>} />
          <Route path="/profile" element={<Profile />} />
        </Route>
        <Route
          path="/room/:roomId"
          element={
            <RequireAuth>
              <ToastProvider>
                <Meeting />
              </ToastProvider>
            </RequireAuth>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}
